import type { GermanyTimeZoneKey } from '@/types';

export function timeInMilliseconds(): number {
    return new Date().getTime();
}

export function delay(time) {
    return new Promise((resolve) => setTimeout(resolve, time));
}

export function getTimeZoneOffsetMinutes(date: Date, timeZone: string): number {
    // Convert the same instant to "wall clock" time in the target timeZone,
    // then interpret that wall clock time as if it were UTC.
    // The delta between both gives the timezone offset.
    const dateTimeFormat = new Intl.DateTimeFormat('de-DE', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });

    const parts = dateTimeFormat.formatToParts(date);
    const values: Record<string, string> = {};
    for (const part of parts) {
        if (part.type !== 'literal') values[part.type] = part.value;
    }

    const asUtc = Date.UTC(
        Number(values.year),
        Number(values.month) - 1,
        Number(values.day),
        Number(values.hour),
        Number(values.minute),
        Number(values.second)
    );

    return (asUtc - date.getTime()) / 60000;
}

export const GERMANY_TIME_ZONE = 'Europe/Berlin';

export function isGermanSummerTime(date: Date = new Date()): boolean {
    return getTimeZoneOffsetMinutes(date, GERMANY_TIME_ZONE) === 120;
}

export function getGermanyTimeZoneKey(date: Date = new Date()): GermanyTimeZoneKey {
    return isGermanSummerTime(date) ? 'cest' : 'cet';
}

export function getGermanyUtcDifference(date: Date = new Date()): string {
    return isGermanSummerTime(date) ? 'UTC+2' : 'UTC+1';
}

export function getGermanNow(): Date {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('de-DE', {
        timeZone: GERMANY_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });

    const parts = formatter.formatToParts(now);
    const values: Record<string, string> = {};
    for (const part of parts) {
        if (part.type !== 'literal') values[part.type] = part.value;
    }

    return new Date(
        Number(values.year),
        Number(values.month) - 1,
        Number(values.day),
        Number(values.hour),
        Number(values.minute),
        Number(values.second)
    );
}

/**
 * Rounds up a time to the next step increment (e.g., 30 minutes).
 * Examples with stepMinutes=30:
 *   10:00 -> 10:00 (already on step)
 *   10:01 -> 10:30
 *   10:30 -> 10:30 (already on step)
 *   10:31 -> 11:00
 */
export function roundUpToNextTimeStep(date: Date, stepMinutes: number = 30): Date {
    const result = new Date(date);
    const minutes = result.getMinutes();
    const remainder = minutes % stepMinutes;

    if (remainder === 0) {
        if (result.getSeconds() > 0 || result.getMilliseconds() > 0) {
            result.setMinutes(minutes + stepMinutes);
        }
    } else {
        result.setMinutes(minutes + (stepMinutes - remainder));
    }

    result.setSeconds(0);
    result.setMilliseconds(0);

    return result;
}

export function toDateTimeLocalString(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return (
        date.getFullYear() +
        '-' +
        pad(date.getMonth() + 1) +
        '-' +
        pad(date.getDate()) +
        'T' +
        pad(date.getHours()) +
        ':' +
        pad(date.getMinutes())
    );
}
